import React, {useEffect, useState} from 'react';
import './App.css';
import Navigation from './components/Navigation';
import HeroBanner from './components/HeroBanner';
import InfoSection from './components/InfoSection';
import WorkFormatsSection from './components/WorkFormatsSection';
import AboutMeSection from './components/AboutMeSection';
import PartnersSection from './components/PartnersSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import PortfolioPage from './pages/portfolio/PortfolioPage';
import { initYandexMetrika } from './utils/yandexMetrika';
import MainPageSkeleton from './components/skeletons/MainPageSkeleton';

function App() {
    const [showPortfolio, setShowPortfolio] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    // Инициализация Яндекс Метрики и отслеживание UTM-меток
    useEffect(() => {
        // Метрика уже инициализирована в index.html, только отслеживаем UTM
        const trackUTM = () => {
            const { trackUTMParams } = require('./utils/yandexMetrika');
            // Проверяем UTM-метки с задержкой, чтобы Метрика успела загрузиться
            setTimeout(() => {
                trackUTMParams();
            }, 200);
        };
        
        // Отслеживаем UTM-метки при загрузке
        trackUTM();
        
        // Отслеживаем UTM-метки при изменении URL (для SPA)
        const handleLocationChange = () => {
            setTimeout(() => {
                const { trackUTMParams } = require('./utils/yandexMetrika');
                trackUTMParams();
            }, 100);
        };
        
        // Отслеживаем изменения URL
        window.addEventListener('popstate', handleLocationChange);
        
        return () => {
            window.removeEventListener('popstate', handleLocationChange);
        };
    }, []);

    // Simulate page loading
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 800);
        return () => clearTimeout(timer);
    }, []);

    // Обработка события открытия портфолио
    useEffect(() => {
        const handleOpenPortfolio = () => {
            setShowPortfolio(true);
            // Добавляем запись в историю браузера
            window.history.pushState({ page: 'portfolio' }, '', '#portfolio');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        // Обработка кнопки "Назад" в браузере
        const handlePopState = (event: PopStateEvent) => {
            if (showPortfolio) {
                setShowPortfolio(false);
            }
        };

        window.addEventListener('openPortfolio', handleOpenPortfolio);
        window.addEventListener('popstate', handlePopState);
        
        // Проверяем, если открыта страница портфолио по URL
        if (window.location.hash === '#portfolio') {
            setShowPortfolio(true);
        }

        return () => {
            window.removeEventListener('openPortfolio', handleOpenPortfolio);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showPortfolio]);

    // Simple scroll animation trigger
    useEffect(() => {
        const handleScroll = () => {
            const reveals = document.querySelectorAll('.reveal');
            reveals.forEach(reveal => {
                const windowHeight = window.innerHeight;
                const revealTop = reveal.getBoundingClientRect().top;
                if (revealTop < windowHeight - 100) {
                    reveal.classList.add('active');
                }
            });
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Mouse tracking for glass card hover effect
    useEffect(() => {
        const glassCards = document.querySelectorAll('.glass-card');
        
        const handleMouseMove = (e: MouseEvent) => {
            const card = e.currentTarget as HTMLElement;
            const rect = card.getBoundingClientRect();
            const x = e.clientX - rect.left; // Mouse X position relative to card
            const y = e.clientY - rect.top;  // Mouse Y position relative to card
            
            // Set CSS variables as percentage
            card.style.setProperty('--mouse-x', `${(x / rect.width) * 100}%`);
            card.style.setProperty('--mouse-y', `${(y / rect.height) * 100}%`);
        };

        const handleMouseLeave = (e: MouseEvent) => {
            const card = e.currentTarget as HTMLElement;
            // Reset to center when mouse leaves
            card.style.setProperty('--mouse-x', '50%');
            card.style.setProperty('--mouse-y', '50%');
        };

        glassCards.forEach(card => {
            card.addEventListener('mousemove', handleMouseMove as EventListener);
            card.addEventListener('mouseleave', handleMouseLeave as EventListener);
        });

        return () => {
            glassCards.forEach(card => {
                card.removeEventListener('mousemove', handleMouseMove as EventListener);
                card.removeEventListener('mouseleave', handleMouseLeave as EventListener);
            });
        };
    }, []);

    const handleBackFromPortfolio = () => {
        if (window.history.state && window.history.state.page === 'portfolio') {
            window.history.back();
        } else {
            setShowPortfolio(false);
            window.history.replaceState(null, '', window.location.pathname);
        }
    };

    if (showPortfolio) {
        return (
            <div className="App">
                <Navigation showPortfolio={true} onBack={handleBackFromPortfolio} />
                <PortfolioPage onBack={handleBackFromPortfolio} />
                <Footer />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="App">
                <Navigation />
                <MainPageSkeleton />
                <Footer />
            </div>
        );
    }

    return (
        <div className="App">
            <Navigation />
            <HeroBanner />

            <main className="content-wrapper">
                <AboutMeSection />
                <InfoSection />
                <WorkFormatsSection />
                <PartnersSection />
                <ContactSection />
            </main>
            <Footer />
        </div>
    );
}

export default App;